import React, { useState, useRef, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

interface EditableNumberProps {
  value: number | null
  onSave: (value: number | null) => Promise<void>
  min?: number
  max?: number
  placeholder?: string
  className?: string
  unit?: string
}

export default function EditableNumber({ 
  value, 
  onSave, 
  min, 
  max, 
  placeholder = "Klicken zum Bearbeiten",
  className = "",
  unit = ""
}: EditableNumberProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState(value?.toString() || '')
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setEditValue(value?.toString() || '')
  }, [value])

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleSave = async () => {
    const numericValue = editValue.trim() === '' ? null : parseInt(editValue.trim())
    
    // Validate range if min/max specified
    if (numericValue !== null) {
      if (min !== undefined && numericValue < min) {
        setEditValue(value?.toString() || '')
        setIsEditing(false)
        return
      }
      if (max !== undefined && numericValue > max) {
        setEditValue(value?.toString() || '')
        setIsEditing(false)
        return
      }
    }

    if (numericValue === value) {
      setIsEditing(false)
      return
    }

    try {
      setIsSaving(true)
      await onSave(numericValue)
      setIsEditing(false)
    } catch (error) {
      console.error('Error saving:', error)
      // Reset to original value on error
      setEditValue(value?.toString() || '')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditValue(value?.toString() || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  if (isEditing) {
    return (
      <div className="relative">
        <input
          ref={inputRef}
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          min={min}
          max={max}
          placeholder={placeholder}
          className={`w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`}
          disabled={isSaving}
        />
        {isSaving && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div 
      className={`cursor-pointer hover:bg-gray-50 px-2 py-1 rounded transition-colors ${className}`}
      onClick={() => setIsEditing(true)}
      title="Klicken zum Bearbeiten"
    >
      {value !== null ? `${value}${unit}` : (
        <span className="text-gray-400 italic">{placeholder}</span>
      )}
    </div>
  )
}
